import React, { useEffect, useState, useMemo } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Table,
  FormFeedback,
  Spinner,
} from 'reactstrap';
import Select from 'react-select';
import { useFormik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { FormType } from 'components/Modals/formTypes';
import { GenericFormModal } from 'components/Modals/AddGenericModal';
import { customSelectStyles } from 'components/Common/SelectStyle';
import Breadcrumb from 'components/Common/Breadcrumb';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useCreateMutation,
  useUpdateMutation,
  useListQuery,
  useDetailQuery,
  useUpdatePatchMutation,
  usePaginatedListQuery,
} from 'http-client/hooks/useCrud';
import { API_ENDPOINTS } from 'http-client/httpConstants';
import { useShop } from 'context/shopContext';
import { handleFormErrors } from 'utils/formErrorHandler';
import { SHOP_PATHS } from 'routes/routesConstants';
import { PaymentMethod } from 'schema/sell-inventory';
import { generateInvoiceNumber } from 'utils/dateFormatter';
import { useDebounce } from 'hooks/useDebounce';

export interface InventoryItem {
  shopId: string;
  selectedValue?: { label: string; value: string; quantity: number; sellPrice: number } | null;
  shopItemId: string;
  shopItemName: string;
  location: string;
  shelfLevel: number;
  quantity: number;
  sellPrice: number;
  isActive: boolean;
  deletedAt: boolean;
  updatedAt: string;
  nearestExpiryDate: string;
}

interface ItemRow {
  id?: string;
  shopItemId: string;
  selectedValue?: { label: string; value: string; quantity: number; sellPrice: number } | null;
  quantity: number | string;
  unitPrice: number | string;
  discountValue: number | string | null;
  taxAmount: number | string | null;
  netValue: number | string;
  availableQty?: number | string;
}

interface SellInvoiceData {
  invoiceNumber?: string;
  invoiceDate: string;
  paymentMethod: string;
  remarks: string | null;
  invoiceDiscountValue: number;
  discount?: number;
  totalAmount: number;
  items: ItemRow[];
}

// Validation schema for invoice header
const sellInvoiceValidationSchema = Yup.object({
  invoiceNumber: Yup.string().nullable().trim(),
  invoiceDate: Yup.date()
    .required('Invoice date is required')
    .max(new Date(), 'Invoice date cannot be in the future'),
  paymentMethod: Yup.string().required('Payment method is required'),
  remarks: Yup.string().trim().max(500, 'Remarks must be at most 500 characters').nullable(),
  invoiceDiscountValue: Yup.number().min(0, 'Invoice discount cannot be negative'),
});

const itemValidationSchema = Yup.object({
  shopItemId: Yup.string().required('Item is required'),
  quantity: Yup.number()
    .required('Quantity is required')
    .min(1, 'Quantity must be at least 1')
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  unitPrice: Yup.number()
    .required('Unit price is required')
    .min(0.01, 'Unit price must be greater than 0')
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  discountValue: Yup.number()
    .min(0, 'Cannot be negative')
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  taxAmount: Yup.number()
    .min(0, 'Cannot be negative')
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
});

const SellInvoiceEntry: React.FC<{ invoiceId?: string }> = ({ invoiceId }) => {
  const { shop } = useShop();
  const navigate = useNavigate();
  const { id: shopId } = shop;

  const [modalFormType, setModalFormType] = useState<FormType | null>(null);
  const [items, setItems] = useState<ItemRow[]>([]);
  const [itemErrors, setItemErrors] = useState<{ [key: number]: { [key: string]: string } }>({});
  const [itemTouched, setItemTouched] = useState<{ [key: number]: { [key: string]: boolean } }>({});

  // Search terms per row
  const [searchTerms, setSearchTerms] = useState<{ [key: number]: string }>({});

  // Get the latest search term for debouncing (single query)
  const activeSearchTerm = useMemo(() => {
    const values = Object.values(searchTerms).filter(Boolean);
    return values.length > 0 ? values[values.length - 1] : '';
  }, [searchTerms]);

  const debouncedSearchTerm = useDebounce(activeSearchTerm, 300);

  // Single top-level paginated query for inventory items
  const { data: inventorySearchData, isLoading: isSearchingInventory } = usePaginatedListQuery(
    API_ENDPOINTS.TENANT.INVENTORIES.PAGINATED.replace(':shopId', shopId || ''),
    {
      pageNumber: 1,
      pageSize: 50,
      search: debouncedSearchTerm,
      shopId: shop?.id,
    },
    { enabled: (debouncedSearchTerm?.length || 0) > 1 },
  );

  const itemSelectOptions = useMemo(() => {
    return (
      inventorySearchData?.items
        ?.filter((inv: any) => inv.isActive)
        ?.map((inv: any) => ({
          label: inv.shopItemName,
          value: inv.shopItemId,
          quantity: inv.quantity,
          sellPrice: inv.sellPrice,
        })) || []
    );
  }, [inventorySearchData]);

  const isEditing = !!invoiceId;

  const {
    data: sellInvoice,
    isLoading: fetching,
    error: fetchError,
  } = useDetailQuery<SellInvoiceData>(API_ENDPOINTS.TENANT.SELLS.WITH_ID, {
    id: invoiceId,
    // enabled: isEditing,
  });

  const { mutate: createMutation, isPending: createPending } = useCreateMutation(
    API_ENDPOINTS.TENANT.SELLS.BASE,
  );
  const { mutate: updateMutation, isPending: updatePending } = useUpdatePatchMutation(
    API_ENDPOINTS.TENANT.SELLS.WITH_ID,
    API_ENDPOINTS.TENANT.SELLS.BASE,
  );

  // Formik for invoice header
  const formik = useFormik<SellInvoiceData>({
    initialValues: {
      invoiceNumber: sellInvoice?.invoiceNumber || '',
      invoiceDate:
        (sellInvoice?.invoiceDate
          ? new Date(sellInvoice.invoiceDate).toISOString().split('T')[0]
          : new Date().toISOString().split('T')[0]) ?? '',
      paymentMethod: sellInvoice?.paymentMethod || PaymentMethod.Cash,
      remarks: sellInvoice?.remarks || '',
      invoiceDiscountValue: sellInvoice?.discount || 0,
      totalAmount: sellInvoice?.totalAmount || 0,
      items: items || [],
    },
    validationSchema: sellInvoiceValidationSchema,
    enableReinitialize: true,
    onSubmit: async (values, formikHelpers: FormikHelpers<SellInvoiceData>) => {
      // Validate all items
      const validatedItems = await validateAllItems();
      if (!validatedItems) {
        return; // Validation failed
      }

      const totalNetAmount = items.reduce((sum, i) => sum + (Number(i.netValue) || 0), 0);
      const calculatedTotal = totalNetAmount - values.invoiceDiscountValue;
      if (Math.abs(values.totalAmount - calculatedTotal) > 0.01) {
        formikHelpers.setFieldError('totalAmount', 'Total amount does not match calculated total');
        return;
      }

      const payload = {
        shopId: shop.id,
        invoiceNumber: values?.invoiceNumber?.trim() ?? null,
        invoiceDate: values.invoiceDate,
        paymentMethod: values.paymentMethod.trim(),
        remarks: values.remarks?.trim() || null,
        invoiceDiscountValue: values.invoiceDiscountValue,
        totalAmount: calculatedTotal,
        subTotal: totalNetAmount.toFixed(2),
        items: items.map((item) => ({
          id: item?.id || null,
          shopId: shop.id,
          shopItemId: item.shopItemId,
          ItemNameAtSale: item.selectedValue?.label || '',
          quantity: Number(item.quantity),
          unitPrice: Number(item.unitPrice),
          netValue: Number(item.netValue),
          discountAmount: item.discountValue ? Number(item.discountValue) : null,
          taxAmount: item.taxAmount ? Number(item.taxAmount) : null,
        })),
      };

      const onSuccess = () => {
        navigate(SHOP_PATHS.MEDICINE_SELL_INVOICES.replace(':shopId', shop.id || ''));
      };

      const onError = (error: any) => {
        handleFormErrors<SellInvoiceData>(error, formikHelpers);
      };

      if (!isEditing) {
        createMutation(payload, { onSuccess, onError });
      } else {
        updateMutation({ data: payload, params: { id: invoiceId } }, { onSuccess, onError });
      }
    },
  });

  // Auto-calculate totalAmount
  useEffect(() => {
    const totalNetAmount = items.reduce((sum, i) => sum + (Number(i.netValue) || 0), 0);
    const calculatedTotal = totalNetAmount - (formik.values.invoiceDiscountValue || 0);
    formik.setFieldValue('totalAmount', calculatedTotal);
  }, [items, formik.values.invoiceDiscountValue]);

  console.log(sellInvoice?.items, 'sellInvoice items');
  useEffect(() => {
    if (sellInvoice?.items) {
      setItems(
        sellInvoice.items.map((i: any) => ({
          id: i.id,
          shopItemId: i.shopItemId || '',
          selectedValue: {
            label: i.itemName,
            value: i.shopItemId,
            sellPrice: i?.currentUnitPrice ?? i.unitPrice,
            quantity: i.currentAvailableQuantity ? i.currentAvailableQuantity.toString() : '',
          },
          quantity: i.quantity ? i.quantity.toString() : '',
          unitPrice: i.unitPrice ? i.unitPrice.toString() : '',
          currentUnitPrice: i?.currentUnitPrice ?? i.unitPrice,
          discountValue: i.discountAmount ? i.discountAmount.toString() : '',
          taxAmount: i.taxAmount ? i.taxAmount.toString() : '',
          netValue: i.netValue ? i.netValue.toString() : '',
          availableQty: i.currentAvailableQuantity ? i.currentAvailableQuantity.toString() : '',
        })),
      );
    }
  }, [sellInvoice?.items]);

  // On mount, ensure at least one row for new invoice
  useEffect(() => {
    if (items.length === 0 && !isEditing) {
      addNewRow();
    }
  }, [isEditing]);

  const addNewRow = () => {
    setItems((prev) => [
      ...prev,
      {
        shopItemId: '',
        quantity: '',
        unitPrice: '',
        discountValue: '',
        taxAmount: '',
        netValue: '',
        availableQty: '',
      },
    ]);
  };

  const removeRow = (index: number) => {
    if (items.length > 1) {
      const updated = [...items];
      updated.splice(index, 1);
      setItems(updated);

      // Remove and reindex errors
      const newErrors = { ...itemErrors };
      delete newErrors[index];
      const reindexedErrors: { [key: number]: { [key: string]: string } } = {};
      Object.keys(newErrors).forEach((key) => {
        const idx = parseInt(key);
        if (idx > index) {
          reindexedErrors[idx - 1] = newErrors[idx] || {};
        } else {
          reindexedErrors[idx] = newErrors[idx] || {};
        }
      });
      setItemErrors(reindexedErrors);

      // Remove and reindex touched
      const newTouched = { ...itemTouched };
      delete newTouched[index];
      const reindexedTouched: { [key: number]: { [key: string]: boolean } } = {};
      Object.keys(newTouched).forEach((key) => {
        const idx = parseInt(key);
        if (idx > index) {
          reindexedTouched[idx - 1] = newTouched[idx] || {};
        } else {
          reindexedTouched[idx] = newTouched[idx] || {};
        }
      });
      setItemTouched(reindexedTouched);

      // Remove search term
      setSearchTerms((prev) => {
        const newTerms = { ...prev };
        delete newTerms[index];
        return newTerms;
      });
    }
  };

  const handleItemChange = (index: number, field: keyof ItemRow, value: any) => {
    const updatedItems = [...items];
    const item = { ...updatedItems[index], [field]: value };

    // Recalculate netValue
    const quantity = Number(item.quantity) || 0;
    const unitPrice = Number(item.unitPrice) || 0;
    const gross = quantity * unitPrice;
    const discountValueNum = Number(item.discountValue) || 0;
    const tax = Number(item.taxAmount) || 0;
    const net = gross - discountValueNum + tax;
    item.netValue = net.toFixed(2);

    // Set availableQty and unitPrice if itemId changed
    if (field === 'shopItemId') {
      const selected = itemSelectOptions?.find((opt: any) => opt.value === item.shopItemId);
      item.selectedValue = selected ?? item.selectedValue ?? null;

      item.availableQty = selected?.quantity ? selected.quantity.toString() : '';
      if (selected?.sellPrice) {
        item.unitPrice = selected.sellPrice.toString();
      }
    }

    updatedItems[index] = item;
    setItems(updatedItems);

    validateItem(index);
  };

  const handleInventorySearchChange = (index: number, inputValue: string) => {
    setSearchTerms((prev) => ({ ...prev, [index]: inputValue }));
  };

  const handleInventorySelect = (index: number, selected: { value: string } | null) => {
    handleItemChange(index, 'shopItemId', selected?.value || '');
    // Clear search term after selection
    setSearchTerms((prev) => {
      const nt = { ...prev };
      delete nt[index];
      return nt;
    });
  };

  const handleItemBlur = (index: number, field: keyof ItemRow) => {
    setItemTouched((prev) => {
      const rowTouched = prev[index] || {};
      return { ...prev, [index]: { ...rowTouched, [field]: true } };
    });
    validateItem(index);
  };

  const validateItem = async (index: number) => {
    try {
      await itemValidationSchema.validate(items[index], { abortEarly: false });
      // Additional check for available quantity
      const qty = Number(items[index].quantity) || 0;
      const avail = Number(items[index].availableQty) || 0;
      if (qty > avail) {
        throw new Error('Quantity exceeds available stock');
      }
      setItemErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[index];
        return newErrors;
      });
    } catch (err: any) {
      const errors: { [key: string]: string } = {};
      if (err.name === 'ValidationError' && err.inner) {
        err.inner.forEach((error: any) => {
          if (error.path) {
            errors[error.path] = error.message;
          }
        });
      } else {
        errors.quantity = err.message || 'Validation error';
      }
      setItemErrors((prev) => ({ ...prev, [index]: errors }));
    }
  };

  const validateAllItems = async (): Promise<boolean> => {
    const errors: { [key: number]: { [key: string]: string } } = {};
    let hasErrors = false;

    for (let i = 0; i < items.length; i++) {
      try {
        await itemValidationSchema.validate(items[i], { abortEarly: false });
        // Additional check for available quantity
        const qty = Number(items[i].quantity) || 0;
        const avail = Number(items[i].availableQty) || 0;
        if (qty > avail) {
          hasErrors = true;
          errors[i] = { quantity: 'Quantity exceeds available stock' };
        }
      } catch (err: any) {
        hasErrors = true;
        if (err.inner) {
          const itemErrors: { [key: string]: string } = {};
          err.inner.forEach((error: any) => {
            if (error.path) {
              itemErrors[error.path] = error.message;
            }
          });
          errors[i] = itemErrors;
        } else {
          errors[i] = { quantity: err.message || 'Validation error' };
        }
      }
    }

    setItemErrors(errors);

    if (hasErrors) {
      alert('Please fix validation errors in the items table');
    }

    return !hasErrors;
  };

  const handleModalSuccess = (newEntity: any) => {
    const newOption = { label: newEntity.name, value: newEntity.id };
    // Handle modal success if needed
    setModalFormType(null);
  };

  const totalSoldItems = items.length;
  const totalNetAmount = items.reduce((sum, item) => sum + (Number(item.netValue) || 0), 0);
  const totalTaxPaid = items.reduce((sum, item) => sum + (Number(item.taxAmount) || 0), 0);
  // Helper function to check if field has error
  const hasItemError = (rowIndex: number, field: keyof ItemRow): boolean => {
    return !!itemTouched[rowIndex]?.[field] && !!itemErrors[rowIndex]?.[field];
  };

  // Custom select styles with error state
  const getSelectStyles = (hasError: boolean) => ({
    ...customSelectStyles,
    control: (base: any, state: any) => ({
      ...customSelectStyles.control(base),
      borderColor: hasError ? '#dc3545' : base.borderColor,
      '&:hover': {
        borderColor: hasError ? '#dc3545' : base.borderColor,
      },
    }),
  });

  const handleTableKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addNewRow();
    }
  };

  if (fetching) {
    return (
      <div className="page-content">
        <Container fluid className="mt-4">
          <Spinner />
        </Container>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="page-content">
        <Container fluid className="mt-4">
          <div>Error loading invoice: {fetchError.message}</div>
        </Container>
      </div>
    );
  }

  return (
    <div>
      <Container fluid className="mt-4">
        <Breadcrumb
          title="Sales"
          breadcrumbItem={isEditing ? 'Edit Sale Invoice' : 'Sale Invoice Entry'}
        />
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <Form onSubmit={formik.handleSubmit}>
                  <Row>
                    {isEditing && (
                      <Col md={3}>
                        <FormGroup>
                          <Label>Invoice Number *</Label>
                          <Input
                            type="text"
                            name="invoiceNumber"
                            value={formik.values.invoiceNumber}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            placeholder="e.g. INV-00123"
                            disabled={true}
                            invalid={formik.touched.invoiceNumber && !!formik.errors.invoiceNumber}
                          />
                          <FormFeedback>{formik.errors.invoiceNumber}</FormFeedback>
                        </FormGroup>
                      </Col>
                    )}
                    <Col md={3}>
                      <FormGroup>
                        <Label>Date *</Label>
                        <Input
                          type="date"
                          name="date"
                          value={formik.values.invoiceDate}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={formik.touched.invoiceDate && !!formik.errors.invoiceDate}
                          disabled
                        />
                        <FormFeedback>{formik.errors.invoiceDate}</FormFeedback>
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Payment Method *</Label>
                        <Input
                          type="select"
                          name="paymentMethod"
                          value={formik.values.paymentMethod}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={formik.touched.paymentMethod && !!formik.errors.paymentMethod}
                        >
                          <option value="">Select a payment method</option>
                          {Object.values(PaymentMethod).map((method) => (
                            <option key={method} value={method}>
                              {method}
                            </option>
                          ))}
                        </Input>
                        <FormFeedback>{formik.errors.paymentMethod}</FormFeedback>
                      </FormGroup>
                    </Col>
                  </Row>

                  <h5 className="mb-3 mt-4">Items</h5>
                  <Row className="align-items-center mb-3">
                    <Col xs="6">
                      <Button color="secondary" onClick={addNewRow} type="button">
                        + Add Item
                      </Button>
                    </Col>
                    <Col xs="6" className="text-end">
                      <Button
                        color="success"
                        onClick={() => setModalFormType(FormType.MEDICINE)}
                        type="button"
                      >
                        + Add Medicine
                      </Button>
                    </Col>
                  </Row>

                  <div
                    style={{
                      overflowX: 'auto',
                      maxHeight: '500px',
                      minHeight: '100px',
                    }}
                  >
                    <Table bordered responsive size="sm" className="table-sticky">
                      <thead
                        style={{
                          position: 'sticky',
                          top: 0,
                          background: '#f7f7f7',
                          zIndex: 1,
                        }}
                      >
                        <tr>
                          <th style={{ minWidth: '200px' }}>Item *</th>
                          <th style={{ minWidth: '120px' }}>Unit Price *</th>
                          <th style={{ minWidth: '100px' }}>Qty *</th>
                          <th style={{ minWidth: '120px' }}>Discount Value</th>
                          <th style={{ minWidth: '100px', color: '#34c38f' }}>Tax Amount</th>
                          <th style={{ minWidth: '120px', color: '#f46a6a' }}>Net Value</th>
                          <th style={{ minWidth: '100px' }}>Available Qty</th>
                          <th style={{ minWidth: '80px' }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item, idx) => (
                          <tr key={idx}>
                            <td style={{ padding: 0, height: '30px' }}>
                              <Select
                                value={item.selectedValue || null}
                                onChange={(sel) => handleInventorySelect(idx, sel)}
                                onInputChange={(val) => handleInventorySearchChange(idx, val)}
                                onBlur={() => handleItemBlur(idx, 'shopItemId')}
                                onKeyDown={handleTableKeyDown}
                                options={itemSelectOptions}
                                isLoading={isSearchingInventory}
                                placeholder="Search and select item..."
                                styles={getSelectStyles(hasItemError(idx, 'shopItemId'))}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                noOptionsMessage={() =>
                                  isSearchingInventory ? 'Searching...' : 'Type to search items...'
                                }
                              />
                              {hasItemError(idx, 'shopItemId') && (
                                <FormFeedback style={{ display: 'block', fontSize: '0.75rem' }}>
                                  {itemErrors[idx]?.shopItemId}
                                </FormFeedback>
                              )}
                            </td>
                            <td
                              style={{
                                padding: 0,
                                border: hasItemError(idx, 'unitPrice')
                                  ? '2px solid #dc3545'
                                  : 'none',
                              }}
                            >
                              <Input
                                type="number"
                                step="0.01"
                                style={{
                                  border: 'none',
                                  height: '100%',
                                  width: '100%',
                                  padding: '0 4px',
                                  fontSize: '0.875rem',
                                }}
                                min={0.01}
                                value={item.unitPrice}
                                onChange={(e) =>
                                  handleItemChange(idx, 'unitPrice', +e.target.value)
                                }
                                onBlur={() => handleItemBlur(idx, 'unitPrice')}
                                onKeyDown={handleTableKeyDown}
                                bsSize="sm"
                              />
                              {hasItemError(idx, 'unitPrice') && (
                                <FormFeedback style={{ display: 'block', fontSize: '0.75rem' }}>
                                  {itemErrors[idx]?.unitPrice}
                                </FormFeedback>
                              )}
                            </td>
                            <td
                              style={{
                                padding: 0,
                                border: hasItemError(idx, 'quantity')
                                  ? '2px solid #dc3545'
                                  : 'none',
                              }}
                            >
                              <Input
                                type="number"
                                style={{
                                  border: 'none',
                                  height: '100%',
                                  width: '100%',
                                  padding: '0 4px',
                                  fontSize: '0.875rem',
                                }}
                                min={1}
                                value={item.quantity}
                                onChange={(e) => handleItemChange(idx, 'quantity', +e.target.value)}
                                onBlur={() => handleItemBlur(idx, 'quantity')}
                                onKeyDown={handleTableKeyDown}
                                bsSize="sm"
                              />
                              {hasItemError(idx, 'quantity') && (
                                <FormFeedback style={{ display: 'block', fontSize: '0.75rem' }}>
                                  {itemErrors[idx]?.quantity}
                                </FormFeedback>
                              )}
                            </td>
                            <td
                              style={{
                                padding: 0,
                                border: hasItemError(idx, 'discountValue')
                                  ? '2px solid #dc3545'
                                  : 'none',
                              }}
                            >
                              <Input
                                type="number"
                                style={{
                                  border: 'none',
                                  height: '100%',
                                  width: '100%',
                                  padding: '0 4px',
                                  fontSize: '0.875rem',
                                }}
                                min={0}
                                value={item.discountValue || ''}
                                onChange={(e) =>
                                  handleItemChange(idx, 'discountValue', +e.target.value)
                                }
                                onBlur={() => handleItemBlur(idx, 'discountValue')}
                                onKeyDown={handleTableKeyDown}
                                bsSize="sm"
                              />
                              {hasItemError(idx, 'discountValue') && (
                                <FormFeedback style={{ display: 'block', fontSize: '0.75rem' }}>
                                  {itemErrors[idx]?.discountValue}
                                </FormFeedback>
                              )}
                            </td>
                            <td
                              style={{
                                padding: 0,
                                border: hasItemError(idx, 'taxAmount')
                                  ? '2px solid #dc3545'
                                  : 'none',
                              }}
                            >
                              <Input
                                type="number"
                                style={{
                                  border: 'none',
                                  height: '100%',
                                  width: '100%',
                                  padding: '0 4px',
                                  fontSize: '0.875rem',
                                }}
                                min={0}
                                value={item.taxAmount || ''}
                                onChange={(e) =>
                                  handleItemChange(idx, 'taxAmount', +e.target.value)
                                }
                                onBlur={() => handleItemBlur(idx, 'taxAmount')}
                                onKeyDown={handleTableKeyDown}
                                bsSize="sm"
                              />
                              {hasItemError(idx, 'taxAmount') && (
                                <FormFeedback style={{ display: 'block', fontSize: '0.75rem' }}>
                                  {itemErrors[idx]?.taxAmount}
                                </FormFeedback>
                              )}
                            </td>
                            <td style={{ padding: 0 }}>
                              <Input
                                type="number"
                                style={{
                                  border: 'none',
                                  height: '100%',
                                  width: '100%',
                                  padding: '0 4px',
                                  fontSize: '0.875rem',
                                  backgroundColor: '#f0f0f0',
                                }}
                                readOnly
                                value={item.netValue}
                                bsSize="sm"
                              />
                            </td>
                            <td style={{ padding: 0 }}>
                              <Input
                                type="number"
                                style={{
                                  border: 'none',
                                  height: '100%',
                                  width: '100%',
                                  padding: '0 4px',
                                  fontSize: '0.875rem',
                                  backgroundColor: '#f0f0f0',
                                }}
                                readOnly
                                value={item.availableQty}
                                bsSize="sm"
                              />
                            </td>
                            <td className="text-center" style={{ padding: '2px' }}>
                              <Button
                                color="danger"
                                size="sm"
                                onClick={() => removeRow(idx)}
                                disabled={items.length <= 1}
                                style={{ minWidth: '40px' }}
                                type="button"
                              >
                                ✕
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>

                  <Row className="justify-content-end">
                    <Col xs="12" sm="8" md="6" lg="4">
                      <CardBody className="py-2 px-3">
                        {/* Total Amount */}
                        <div className="d-flex justify-content-between border-bottom pt-2 mb-3 text-blue">
                          <strong>Total Payable Amount:</strong>
                          <h4 className="fw-bold">
                            {(totalNetAmount - formik.values.invoiceDiscountValue).toFixed(2)}
                          </h4>
                        </div>

                        {/* Total Sold Items */}
                        <div className="d-flex justify-content-between mb-2">
                          <strong>Total Sold Items:</strong>
                          <span>{totalSoldItems}</span>
                        </div>

                        {/* Subtotal */}
                        <div className="d-flex justify-content-between mb-2 text-danger">
                          <strong>Subtotal:</strong>
                          <span className="fw-bold">{totalNetAmount.toFixed(2)}</span>
                        </div>

                        {/* Total Tax Paid */}
                        <div className="d-flex justify-content-between mb-2 text-success">
                          <strong>Total Tax:</strong>
                          <span>{totalTaxPaid.toFixed(2)}</span>
                        </div>

                        {/* Invoice Discount Input */}
                        <FormGroup className="mb-2">
                          <Label>Overall Discount on Invoice</Label>
                          <Input
                            type="number"
                            name="invoiceDiscountValue"
                            value={formik.values.invoiceDiscountValue}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            placeholder="0"
                            invalid={
                              formik.touched.invoiceDiscountValue &&
                              !!formik.errors.invoiceDiscountValue
                            }
                          />
                          <FormFeedback>{formik.errors.invoiceDiscountValue}</FormFeedback>
                        </FormGroup>

                        {/* Invoice Discount Display */}
                        <div className="d-flex justify-content-between mb-2">
                          <strong>Invoice Discount:</strong>
                          <span>{formik.values.invoiceDiscountValue.toFixed(2)}</span>
                        </div>

                        {/* Remarks */}
                        <FormGroup>
                          <Label>Remarks</Label>
                          <Input
                            type="textarea"
                            name="remarks"
                            value={formik.values.remarks ?? ''}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={formik.touched.remarks && !!formik.errors.remarks}
                            rows={3}
                          />
                          <FormFeedback>{formik.errors.remarks}</FormFeedback>
                        </FormGroup>
                      </CardBody>
                    </Col>
                  </Row>

                  <div className="mt-4 text-end">
                    <Button
                      color="primary"
                      type="submit"
                      disabled={isEditing ? updatePending : createPending}
                    >
                      {isEditing ? 'Update Invoice' : 'Save Invoice'}
                    </Button>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <GenericFormModal
          isOpen={modalFormType !== null}
          toggle={() => setModalFormType(null)}
          formType={modalFormType as FormType}
          onSuccess={handleModalSuccess}
        />
      </Container>
    </div>
  );
};

export default SellInvoiceEntry;
